// src/features/stock-info/components/ShareFloatChart.tsx
//
// Component Island: Share Float & Liquidity. Fetches the free-float snapshot
// from GET /stock-info/{symbol}/share-float (backed by FMP /stable/shares-float)
// and renders a professional donut/pie chart splitting the ownership into:
//   - Free Float (freeFloat %, already a percentage from FMP)
//   - Institutional (100 - freeFloat, the closely-held remainder)
//
// Each island fetches independently so a slow/failing endpoint never blocks
// the rest of the page.

import { useEffect, useMemo, useState } from 'react';
import {
    Cell,
    Legend,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip,
} from 'recharts';

import { fetchShareFloat, type ShareFloat } from '../api';

const FREE_FLOAT_COLOR = '#3b82f6'; // blue
const INSTITUTIONAL_COLOR = '#8b5cf6'; // violet

const formatShares = (val?: number) =>
    val != null
        ? new Intl.NumberFormat('en-US', {
              notation: 'compact',
              maximumFractionDigits: 2,
          }).format(val)
        : 'N/A';

const formatPct = (val?: number) =>
    val != null ? `${val.toFixed(2)}%` : 'N/A';

export const ShareFloatChart = ({ symbol }: { symbol: string }) => {
    const [data, setData] = useState<ShareFloat | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        setIsLoading(true);
        setError(null);

        fetchShareFloat(symbol)
            .then((res) => {
                if (!cancelled) setData(res ?? null);
            })
            .catch((err) => {
                // eslint-disable-next-line no-console
                console.error(err);
                if (!cancelled) {
                    setError('Share float data unavailable.');
                    setData(null);
                }
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [symbol]);

    const chartData = useMemo(() => {
        if (data?.free_float == null) return [];
        const free = data.free_float;
        const inst =
            data.institutional_float != null
                ? data.institutional_float
                : Math.max(0, 100 - free);
        return [
            { name: 'Free Float', value: free, color: FREE_FLOAT_COLOR },
            { name: 'Institutional', value: inst, color: INSTITUTIONAL_COLOR },
        ];
    }, [data]);

    const hasData = chartData.length > 0;

    return (
        <div className="surface-card border-t-4 border-blue-500 flex flex-col h-full">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-2">
                <h2 className="section-title mb-0">Share Float &amp; Liquidity</h2>
                {data?.outstanding_shares != null && (
                    <span className="px-2.5 py-1 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 text-xs font-bold uppercase tracking-wider">
                        {formatShares(data.outstanding_shares)} Shares Out
                    </span>
                )}
            </div>

            <div className="relative h-[300px] w-full">
                {isLoading && (
                    <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/50 dark:bg-slate-900/50 backdrop-blur-sm">
                        <span className="text-blue-500 font-medium animate-pulse">
                            Loading Float…
                        </span>
                    </div>
                )}

                {!isLoading && (error || !hasData) && (
                    <div className="absolute inset-0 flex items-center justify-center text-sm text-slate-500 dark:text-slate-400">
                        {error || 'No share float data available.'}
                    </div>
                )}

                {!isLoading && !error && hasData && (
                    <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                            <Pie
                                data={chartData}
                                dataKey="value"
                                nameKey="name"
                                cx="50%"
                                cy="50%"
                                innerRadius={70}
                                outerRadius={105}
                                paddingAngle={2}
                                startAngle={90}
                                endAngle={-270}
                                isAnimationActive={false}
                                stroke="none"
                            >
                                {chartData.map((entry) => (
                                    <Cell key={entry.name} fill={entry.color} />
                                ))}
                            </Pie>
                            <Tooltip
                                contentStyle={{
                                    backgroundColor: '#ffffff',
                                    borderColor: '#1e293b',
                                    borderWidth: '2px',
                                    borderRadius: '8px',
                                    color: '#0f172a',
                                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                                }}
                                labelStyle={{
                                    color: '#475569',
                                    marginBottom: '4px',
                                    fontWeight: '600',
                                }}
                                itemStyle={{ color: '#0f172a', fontWeight: '700' }}
                                formatter={(value: number | string, name: string) => [
                                    typeof value === 'number'
                                        ? `${value.toFixed(2)}%`
                                        : value,
                                    name,
                                ]}
                            />
                            <Legend
                                verticalAlign="bottom"
                                iconType="circle"
                                wrapperStyle={{ fontSize: 12, paddingTop: 8 }}
                            />
                        </PieChart>
                    </ResponsiveContainer>
                )}
            </div>

            {hasData && (
                <div className="mt-3 grid grid-cols-2 gap-3 text-center">
                    <div className="rounded-lg bg-slate-50 dark:bg-slate-800/50 p-2">
                        <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">
                            Free Float
                        </p>
                        <p className="text-sm font-bold text-blue-600 dark:text-blue-400">
                            {formatPct(chartData[0]?.value)}
                        </p>
                    </div>
                    <div className="rounded-lg bg-slate-50 dark:bg-slate-800/50 p-2">
                        <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">
                            Float Shares
                        </p>
                        <p className="text-sm font-bold text-slate-700 dark:text-slate-200">
                            {formatShares(data?.float_shares)}
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ShareFloatChart;
